import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useApiClient } from '@/hooks/useApiClient';
import { collectLabelExportData } from '../api/labelExportData';
import type { LabelingExperiment } from '../types/LabelingTypes';
import { hasLabeledData } from '../utils/labelExport';
import { exportLabelsXlsx } from '../utils/labelExportXlsx';

/** `empty`: the fetch succeeded but no tool carries a label, so no file was produced. */
export type LabelExportStatus = 'idle' | 'exporting' | 'empty' | 'error';

export interface LabelExportProgress {
  done: number;
  total: number;
}

export interface UseLabelExportResult {
  /**
   * The export reads every annotator's labels, which the backend only allows to admins (403
   * otherwise) — this flag merely keeps the action out of sight for everyone else.
   */
  canExport: boolean;
  status: LabelExportStatus;
  progress: LabelExportProgress | null;
  exportLabels: (experiments: LabelingExperiment[]) => Promise<void>;
}

/**
 * Exports every annotator's labels of the given experiments as one Excel workbook. Tools nobody
 * labeled are left out, so an export without a single label produces no file (`empty`).
 */
export function useLabelExport(): UseLabelExportResult {
  const http = useApiClient();
  const { tokenService } = useAuth();
  const role = tokenService?.getRole() ?? 'user';
  const canExport = role === 'admin' || role === 'super-admin';
  const [status, setStatus] = useState<LabelExportStatus>('idle');
  const [progress, setProgress] = useState<LabelExportProgress | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const exportLabels = useCallback(
    async (experiments: LabelingExperiment[]) => {
      if (!http || !canExport || controllerRef.current) return;
      const controller = new AbortController();
      controllerRef.current = controller;
      setStatus('exporting');
      setProgress({ done: 0, total: experiments.length });

      try {
        const data = await collectLabelExportData(http, experiments, {
          signal: controller.signal,
          onProgress: (done, total) => {
            if (!controller.signal.aborted) setProgress({ done, total });
          },
        });
        if (controller.signal.aborted) return;
        if (!hasLabeledData(data)) {
          setStatus('empty');
          return;
        }
        await exportLabelsXlsx(data);
        if (!controller.signal.aborted) setStatus('idle');
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        console.warn('[useLabelExport] Export failed:', err);
        setStatus('error');
      } finally {
        controllerRef.current = null;
        if (!controller.signal.aborted) setProgress(null);
      }
    },
    [http, canExport]
  );

  return { canExport, status, progress, exportLabels };
}
